import { type ReactElement, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Drawer, Input, Skeleton } from 'antd';
import { EditOutlined, SaveOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityDetailLayout } from '../../../components/entity-detail/EntityDetailLayout.js';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { EntityTabs } from '../../../components/entity-detail/EntityTabs.js';
import { EntityFieldList } from '../../../components/entity-detail/EntityFieldList.js';
import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityActivityHistory } from '../../../components/entity-detail/EntityActivityHistory.js';
import { ErrorBoundary } from '../../../shared/ErrorBoundary.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { EntityLink } from '../../../shared/ui/EntityLink.js';
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
import { formatMoney, formatPersonName } from '../../../shared/format/index.js';
import { formatDateTime } from '../../../shared/format/entity-detail.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { DealChatPanel } from '../../ai-chat/components/ChatPanel.js';
import { EntityAttachmentsPanel } from '../../attachments/components/EntityAttachmentsPanel.js';
import { EntityMeetingsPanel } from '../../meetings/components/EntityMeetingsPanel.js';
import { useMeetings } from '../../meetings/hooks/useMeetings.js';
import { EntityNotesPanel } from '../../notes/components/EntityNotesPanel.js';
import { useNotes } from '../../notes/hooks/useNotes.js';
import { EntityTasksPanel } from '../../tasks/components/EntityTasksPanel.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { DocumentFormModal } from '../components/DocumentFormModal.js';
import { useDocumentDetail } from '../hooks/useDocuments.js';
import {
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
} from '../hooks/useDocumentMutations.js';
import type { DocumentStatus } from '../api/documents.client.js';

export interface DocumentCardPageProps {
  documentId: string;
}

export function DocumentCardPage({ documentId }: DocumentCardPageProps): ReactElement {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const scope = useCurrentScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { document: record, isLoading, isError, error, refetch } = useDocumentDetail(documentId);
  const [draft, setDraft] = useState<{ id: string; body: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const notes = useNotes({ documentId });
  const tasks = useTasks({ documentId });
  const meetings = useMeetings({ documentId });
  const remove = useDeleteDocumentMutation();
  const update = useUpdateDocumentMutation();
  const prefix = 'crm.features.features.documents.pages.documentcardpage.';
  useEffect(() => {
    setEditOpen(false);
    setChatOpen(false);
  }, [documentId]);
  const openTab = (tab: string): void => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  if (isLoading)
    return (
      <main className="p-6">
        <Skeleton active />
      </main>
    );
  if (isError || !record)
    return (
      <main className="p-6">
        <ErrorState
          title={t(`${prefix}ошибка_загрузки`)}
          error={error}
          onRetry={() => void refetch()}
        />
      </main>
    );
  const body = draft?.id === documentId ? draft.body : record.body;
  const dirty = draft?.id === documentId && draft.body !== record.body;
  const save = async (): Promise<void> => {
    const savedBody = body;
    try {
      await update.mutateAsync({ id: documentId, input: { body: savedBody } });
      // Do not clear newer edits made while the request was in flight.
      setDraft((current) =>
        current?.id === documentId && current.body === savedBody ? null : current,
      );
    } catch {
      void message.error(t(`${prefix}ошибка_сохранения`));
    }
  };
  const tones: Record<DocumentStatus, 'default' | 'processing' | 'success' | 'error'> = {
    draft: 'default',
    sent: 'processing',
    signed: 'success',
    cancelled: 'error',
  };
  const typeLabel = t(
    `${prefix}${{ contract: 'договор', invoice: 'счет', act: 'акт' }[record.type]}`,
  );
  const statusLabel = t(
    `${prefix}${{ draft: 'черновик', sent: 'отправлен', signed: 'подписан', cancelled: 'отменен' }[record.status]}`,
  );
  const linkedTasks = (tasks.data ?? []).filter((task) => task.documentId === record.id);
  const links = [
    ...(record.deal
      ? [
          {
            key: 'deal',
            label: t(`${prefix}сделка_2`),
            value: (
              <EntityLink asTag={false} kind="deal" id={record.deal.id} label={record.deal.title} />
            ),
          },
        ]
      : []),
    ...(record.contact
      ? [
          {
            key: 'contact',
            label: t(`${prefix}контакт`),
            value: (
              <EntityLink
                asTag={false}
                kind="contact"
                id={record.contact.id}
                label={formatPersonName(record.contact.firstName, record.contact.lastName)}
              />
            ),
          },
        ]
      : []),
    ...(record.company
      ? [
          {
            key: 'company',
            label: t(`${prefix}компания`),
            value: (
              <EntityLink
                asTag={false}
                kind="company"
                id={record.company.id}
                label={record.company.name}
              />
            ),
          },
        ]
      : []),
    ...linkedTasks.map((task) => ({
      key: `task-${task.id}`,
      label: t('crm.entity_detail.tasks'),
      value: <EntityLink asTag={false} kind="task" id={task.id} label={task.title} />,
    })),
  ];
  const editor = (
    <section className="entity-section">
      <div className="entity-list-toolbar">
        <h2 className="entity-section-title">{t(`${prefix}текст_документа`)}</h2>
        <Button
          icon={<SaveOutlined aria-hidden />}
          loading={update.isPending}
          disabled={!dirty}
          onClick={() => void save()}
        >
          {t(`${prefix}сохранить`)}
        </Button>
      </div>
      <Input.TextArea
        aria-label={t(`${prefix}текст_документа`)}
        value={body}
        onChange={(event) => setDraft({ id: documentId, body: event.target.value })}
        autoSize={{ minRows: 12 }}
        placeholder={t(`${prefix}начните_писать_текст_документа_или_попроси`)}
      />
    </section>
  );
  return (
    <ErrorBoundary>
      <EntityDetailLayout
        header={
          <>
            <EntityHeader
              backTo="/documents"
              backLabel={t(`${prefix}к_списку_документов`)}
              title={record.title}
              chips={[
                <EntityStatusChip key="type">{typeLabel}</EntityStatusChip>,
                <EntityStatusChip key="status" tone={tones[record.status]}>
                  {statusLabel}
                </EntityStatusChip>,
              ]}
              primary={{
                key: 'chat',
                label: t('crm.entity_detail.open_chat'),
                icon: <MessageOutlined />,
                onClick: () => setChatOpen(true),
              }}
              secondary={[
                {
                  key: 'edit',
                  label: t(`${prefix}редактировать`),
                  icon: <EditOutlined />,
                  onClick: () => setEditOpen(true),
                },
              ]}
              overflow={[
                {
                  key: 'history',
                  label: t(`${prefix}история_документа`),
                  onClick: () => openTab('history'),
                },
                {
                  key: 'delete',
                  label: t(`${prefix}удалить_документ`),
                  danger: true,
                  disabled: remove.isPending,
                  confirm: {
                    title: t(`${prefix}удалить_документ_2`),
                    description: t(`${prefix}документ_пропадет_из_списков_связанные_сде`),
                    okText: t('crm.shared.delete'),
                    cancelText: t('crm.common.cancel'),
                  },
                  onClick: () => remove.mutateAsync(documentId).then(() => navigate('/documents')),
                },
              ]}
            />
            {remove.error || update.error ? (
              <Alert
                type="error"
                showIcon
                title={t('crm.entity_detail.load_error')}
                description={(remove.error ?? update.error)?.message}
              />
            ) : null}
          </>
        }
        sidebar={
          <>
            <section className="entity-panel">
              <h2 className="entity-section-title">{t('crm.entity_detail.summary')}</h2>
              <EntityMetric
                label={t(`${prefix}сумма`)}
                value={
                  record.amount === null ? undefined : (
                    <span style={MONO_NUMERIC_STYLE}>
                      {formatMoney(record.amount, record.currency)}
                    </span>
                  )
                }
              />
              <EntityFieldList
                fields={[
                  { key: 'status', label: t(`${prefix}статус`), value: statusLabel },
                  {
                    key: 'created',
                    label: t('crm.entity_detail.created'),
                    value: formatDateTime(record.createdAt),
                  },
                  {
                    key: 'updated',
                    label: t('crm.entity_detail.updated'),
                    value: formatDateTime(record.updatedAt),
                  },
                ]}
              />
            </section>
            <section className="entity-panel">
              <h2 className="entity-section-title">{t('crm.entity_detail.links')}</h2>
              <EntityFieldList fields={links} />
            </section>
            <section className="entity-panel">
              <h2 className="entity-section-title">{t(`${prefix}детали`)}</h2>
              <EntityFieldList
                fields={[{ key: 'type', label: t(`${prefix}тип`), value: typeLabel }]}
              />
            </section>
          </>
        }
        main={
          <EntityTabs
            items={[
              {
                key: 'overview',
                label: t('crm.entity_detail.overview'),
                content: (
                  <section className="entity-section">
                    <h2 className="entity-section-title">{t(`${prefix}текст_документа`)}</h2>
                    <p className="whitespace-pre-wrap break-words">
                      {body.split(/\r?\n/).slice(0, 10).join('\n')}
                    </p>
                    <Button type="link" onClick={() => openTab('body')}>
                      {t('crm.entity_detail.edit_document_text')}
                    </Button>
                  </section>
                ),
              },
              { key: 'body', label: t(`${prefix}текст_документа`), content: editor },
              {
                key: 'notes',
                label: t('crm.entity_detail.notes'),
                count: notes.data?.length ?? 0,
                content: (
                  <EntityNotesPanel
                    documentId={documentId}
                    title={t(`${prefix}заметки_по_документу`)}
                  />
                ),
              },
              {
                key: 'tasks',
                label: t('crm.entity_detail.tasks'),
                count: tasks.data?.length ?? 0,
                content: (
                  <EntityTasksPanel
                    documentId={documentId}
                    title={t(`${prefix}задачи_по_документу`)}
                  />
                ),
              },
              {
                key: 'meetings',
                label: t('crm.entity_detail.meetings'),
                count: meetings.data?.length ?? 0,
                content: (
                  <EntityMeetingsPanel
                    documentId={documentId}
                    title={t(`${prefix}встречи_по_документу`)}
                  />
                ),
              },
              {
                key: 'files',
                label: t(`${prefix}файлы`),
                content: (
                  <EntityAttachmentsPanel
                    entity="document"
                    entityId={documentId}
                    title={t(`${prefix}файлы_документа`)}
                  />
                ),
              },
              {
                key: 'history',
                label: t('crm.entity_detail.history'),
                content: <EntityActivityHistory entity="document" entityId={documentId} />,
              },
            ]}
          />
        }
      />
      <Drawer
        title={t(`${prefix}ai_помощник`)}
        size="min(760px, 100vw)"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        styles={{ body: { padding: 0, overflow: 'hidden' } }}
      >
        {record.dealId ? (
          <DealChatPanel scope={scope} dealId={record.dealId} />
        ) : (
          <p className="entity-meta">{t(`${prefix}документ_не_привязан_к_сделке_ассистенту_н`)}</p>
        )}
      </Drawer>
      {editOpen ? (
        <DocumentFormModal open document={record} onClose={() => setEditOpen(false)} />
      ) : null}
    </ErrorBoundary>
  );
}
